<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import * as R from 'remeda'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
// @ts-expect-error
import RangeInput from '@/components/lib/RangeInput'
import { getRangeFilterValues } from '../../../../store/api/gql/range_filter_values/queries'
import type { TFilterField } from '../../../../types/api/reports'
import NumberInput from '../../../lib/NumberInput.vue'

type RangeValue = {
  id: string
  cssStyle: string
  cssClass: string
  percentage: number
}

type Props = {
  from: string | number | null
  to: string | number | null
  field: TFilterField
}

const props = defineProps<Props>()
const emits = defineEmits<{
  update: [{ from: number; to: number }]
}>()

const { data, isLoading } = useQuery({
  queryKey: ['range_filter_values', props.field.id],
  queryFn: getRangeFilterValues(props.field.field_name),
  throwOnError: true,
})

const ranges = computed(
  () => data.value?.range_filter_values[props.field.field_name] || { min: 0, max: 1 },
)

const currentRange = computed(() => ranges.value.max - ranges.value.min)

const scale = computed(() => (props.field.ui_value_type.includes('percentage') ? 100 : 1))
const step = computed(() => (props.field.ui_value_type.includes('percentage') ? 1 : 0.1))
const decimals = computed(() => (props.field.ui_value_type.includes('one') ? 1 : 2))

const outputDecimals = computed(() => decimals.value + Math.floor(Math.log10(scale.value)))

const handleRangeInput = ({ values }: { values: RangeValue[] }) => {
  const from = R.round(
    ranges.value.min + currentRange.value * values[0].percentage,
    outputDecimals.value,
  )
  const to = R.round(
    ranges.value.min + currentRange.value * (values[0].percentage + values[1].percentage),
    outputDecimals.value,
  )
  emits('update', { from, to })
}

const rangeValues = ref([
  {
    id: '0',
    cssStyle: `--clr-input-back: var(--clr-black-3); --clr-input-handle: var(--clr-primary-brand-2);`,
    cssClass: '',
    percentage: 0,
  },
  {
    id: '1',
    cssStyle: `--clr-input-back: var(--clr-primary); --clr-input-handle: var(--clr-primary-brand-2)`,
    cssClass: '',
    percentage: 0.5,
  },
  {
    id: '2',
    cssStyle: `--clr-input-back: var(--clr-black-3);`,
    cssClass: '',
    percentage: 0,
  },
])

const l_from = computed(() =>
  R.round(
    Number(props.from !== null ? props.from : ranges.value.min) * scale.value,
    decimals.value,
  ),
)
const l_to = computed(() =>
  R.round(Number(props.to !== null ? props.to : ranges.value.max) * scale.value, decimals.value),
)

watch(
  () => [props.from, props.to, currentRange.value] as const,
  () => {
    const from = props.from !== null ? props.from : ranges.value.min
    const to = props.to !== null ? props.to : ranges.value.max

    const fromPercentage = (Number(from) - ranges.value.min) / currentRange.value
    const toPercentage = (Number(to) - Number(from)) / currentRange.value

    const newRanges = [
      R.set(rangeValues.value[0], 'percentage', fromPercentage),
      R.set(rangeValues.value[1], 'percentage', toPercentage),
      R.set(rangeValues.value[2], 'percentage', 1 - (fromPercentage + toPercentage)),
    ]

    rangeValues.value = [...newRanges]
  },
  { immediate: true },
)
</script>
<template>
  <div
    v-if="isLoading"
    class="f-row mt-1 f-justify-center"
  >
    <LoaderCircles size="large" />
  </div>
  <div
    v-else
    class="f-column"
    style="gap: 0.875rem"
  >
    <div class="f-row">
      <div class="pr-2 weight_500">{{ $t('common.from') }}</div>
      <NumberInput
        class="mr-auto"
        style="--input-width: clamp(1px, 8rem, 100%)"
        :value="l_from"
        :min="ranges.min * scale"
        :max="ranges.max * scale"
        :decimals
        :step
        @input="
          value => {
            const l_from = R.round(value / scale, outputDecimals)
            const l_to = Math.max(Number(to), l_from)
            emits('update', { from: l_from, to: l_to })
          }
        "
      />
      <div class="pr-2 weight_500">{{ $t('common.to') }}</div>
      <NumberInput
        class="mr-auto"
        style="--input-width: clamp(1px, 8rem, 100%)"
        :value="l_to"
        :max="ranges.max * scale"
        :min="ranges.min * scale"
        :decimals
        :step
        @input="
          value => {
            const l_to = R.round(value / scale, outputDecimals)
            const l_from = Math.min(Number(from), l_to)
            emits('update', { to: l_to, from: l_from })
          }
        "
      />
    </div>
    <RangeInput
      class="rainput_variant_circle"
      :values="rangeValues"
      @values:update="handleRangeInput"
    />
  </div>
</template>
